import { Controller } from "@hotwired/stimulus"

// Attached to an element containing FilePond inputs (a form, or a dialog whose
// submit trigger sits outside the form): disables the submit buttons until a
// file has been picked AND FilePond has finished staging it, so the form can't
// be submitted empty or before the upload has been processed. Mirrors the
// online sign-up's form controller, which tracks the same FilePond events for
// its submit button. Files in a [hidden] ancestor don't count — the upload
// dialog toggles between per-type sections and only the visible one submits.
// Buttons are found via the explicit submit target when given, else the
// element's own submit buttons.
export default class extends Controller {
  static targets = ["submit"]

  connect() {
    this.pendingUploads = 0
    // processfilestart (not addfilestart) so every increment belongs to an
    // actual processing attempt: a file that fails loading or validation never
    // reaches the terminal events and would leave the counter stuck positive.
    this.element.addEventListener("FilePond:processfilestart", this.uploadStarted)
    this.element.addEventListener("FilePond:processfile", this.uploadFinished)
    this.element.addEventListener("FilePond:processfileabort", this.uploadFinished)
    this.element.addEventListener("FilePond:removefile", this.refresh)
    this.element.addEventListener("change", this.refresh)
    this.updateSubmitDisabled()
  }

  disconnect() {
    this.element.removeEventListener("FilePond:processfilestart", this.uploadStarted)
    this.element.removeEventListener("FilePond:processfile", this.uploadFinished)
    this.element.removeEventListener("FilePond:processfileabort", this.uploadFinished)
    this.element.removeEventListener("FilePond:removefile", this.refresh)
    this.element.removeEventListener("change", this.refresh)
  }

  // Re-checks now and once more shortly after: FilePond updates the DOM on its
  // own render loop, so right at an event the item state attribute can lag
  // (processing-complete not yet written) and a removed item can linger through
  // its exit animation.
  refresh = () => {
    this.updateSubmitDisabled()
    setTimeout(() => this.updateSubmitDisabled(), 600)
  }

  // Only fully staged files count: a file mid-upload, cancelled, or in error
  // must not enable submission.
  stagedFiles() {
    return [
      ...this.element.querySelectorAll(
        '.filepond--item[data-filepond-item-state="processing-complete"]'
      ),
    ].filter((item) => !item.closest("[hidden]")).length
  }

  submitButtons() {
    if (this.hasSubmitTarget) {
      return this.submitTargets
    }

    return this.element.querySelectorAll(
      "button[type=submit], input[type=submit], button:not([type])"
    )
  }

  uploadFinished = () => {
    this.pendingUploads = Math.max(0, this.pendingUploads - 1)
    this.refresh()
  }

  uploadStarted = () => {
    this.pendingUploads++
    this.refresh()
  }

  updateSubmitDisabled() {
    const disabled = this.pendingUploads > 0 || this.stagedFiles() === 0

    this.submitButtons().forEach((button) => {
      button.disabled = disabled
    })
  }
}
